import { CustomFabricImage } from '../../../global-types/canvas';
import { CanvasDataTypes } from '../../../utils/canvas-utils/canvas-data-types';
import { getIconForLowResImage } from '../../../utils/canvas-utils/get-icon-for-low-res-image';
import { validateImagePpi } from '../../../utils/canvas-utils/validate-image-ppi';
import { addPhotozoneImageMoveConstraint } from './add-photozone-image-move-constraint';
import { doesImageFullyCoverPhotozone } from './apply-image-force-fill-contraints';
import { calculateFabricCoordsCenter } from './calculate-fabric-coords-center';
import { shrinkPt } from './shrink-pt';

type ScalePhotoParams = {
  photo: CustomFabricImage;
  scaleFactor: number;
  canvas: fabric.Canvas;
  originalScale: { x: number | undefined; y: number | undefined };
  isFront: boolean;
  isForceFillEnabled?: boolean;
};

export const scalePhoto = ({
  photo,
  scaleFactor,
  canvas,
  originalScale,
  isFront,
  isForceFillEnabled,
}: ScalePhotoParams) => {
  photo.set({
    scaleX: scaleFactor * (originalScale?.x ?? 1), // we multiply with the original scale to maintain aspect ratio
    scaleY: scaleFactor * (originalScale?.y ?? 1),
  });

  // Check if the active object has a photozone and update its icon visibility
  const photoZoneId = photo.data?.photoZoneId;
  if (photoZoneId) {
    // Find the icon associated with this image in the photozone
    const photoZoneIcon = canvas
      .getObjects()
      .find((obj) => obj.data?.type === CanvasDataTypes.PhotoIcon && obj.data?.parentName === photo.name);
    if (photoZoneIcon) {
      photoZoneIcon.visible = !validateImagePpi(photo);
    }
  } else {
    // get low res icon object for photo
    const lowResIcon = getIconForLowResImage(canvas, photo);
    if (lowResIcon) {
      // update icon visibility based on validateImagePpi
      lowResIcon.visible = !validateImagePpi(photo);
    }
  }

  if (isForceFillEnabled) {
    const canvasObjects = canvas?.getObjects() ?? [];
    const currentPhotozoneObject = canvasObjects.find((object) => object.name === photo.data.photoZoneId);
    if (currentPhotozoneObject && isFront) {
      // Update coordinates after scale change so coverage check uses accurate values
      photo.setCoords();
      currentPhotozoneObject.setCoords();

      // Check if the image still fully covers the photozone after scaling
      const imageCoords = photo.getCoords();
      const photoZoneCoords = currentPhotozoneObject.getCoords();
      const center = calculateFabricCoordsCenter(
        photoZoneCoords as [fabric.Point, fabric.Point, fabric.Point, fabric.Point],
      );
      const photoZoneMarginCoords = (photoZoneCoords as Array<{ x: number; y: number }>).map((coord) =>
        shrinkPt({ center, pt: coord }),
      );

      if (!doesImageFullyCoverPhotozone(imageCoords, photoZoneMarginCoords)) {
        // If the image no longer covers the photozone, center it
        const photoZoneCenter = currentPhotozoneObject.getCenterPoint();
        if (photoZoneCenter) {
          photo.setPositionByOrigin(photoZoneCenter, 'center', 'center');
          photo.setCoords();
        }
      }

      // Remove old 'moving' handlers to prevent stale constraints from accumulating
      photo.off('moving');
      addPhotozoneImageMoveConstraint(photo, currentPhotozoneObject).onCallback();
    }
  }

  canvas.renderAll();
};
